/**
 * Pure formatting / helper logic for the UI (no DOM), unit-tested in Node.
 */
import type { LatLon, ZoneKind } from '../contracts';

/** Proper minus sign for display. */
const MINUS = '−';
/** Narrow no-break space between value and unit. */
export const NBSP = ' ';

/** Normalise an angle to [0, 360). */
export function normDeg(deg: number): number {
  const d = deg % 360;
  return d < 0 ? d + 360 : d;
}

/** Signed smallest angle from `from` to `to`, in (-180, 180]. */
export function shortestDelta(from: number, to: number): number {
  let d = normDeg(to - from);
  if (d > 180) d -= 360;
  return d;
}

/** Rotation (deg) for an arrow pointing to `bearing` when the view looks along `heading`. */
export function relativeBearing(bearing: number, heading: number): number {
  return shortestDelta(heading, bearing);
}

/**
 * Next cumulative CSS rotation so that a transition always takes the short way
 * (e.g. 270 -> 0 animates +90 instead of -270).
 */
export function nextCumulativeRotation(currentCum: number, targetDeg: number): number {
  return currentCum + shortestDelta(normDeg(currentCum), normDeg(targetDeg));
}

const CARDINALS_8 = ['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'] as const;

/** German 8-point compass abbreviation (O = Ost). */
export function cardinal(deg: number): string {
  return CARDINALS_8[Math.round(normDeg(deg) / 45) % 8];
}

/** Distance rounding: < 100 m to 1 m, >= 100 m to 5 m steps. */
export function roundDistance(m: number): number {
  if (!Number.isFinite(m) || m < 0) return 0;
  return m < 99.5 ? Math.round(m) : Math.round(m / 5) * 5;
}

export interface ValueUnit {
  value: string;
  unit: string;
}

/** "85" m, "120" m, "1.25" km (>= 1000 m), "12.4" km (>= 10 km). */
export function formatDistanceParts(m: number | null | undefined): ValueUnit {
  if (m == null || !Number.isFinite(m)) return { value: '–', unit: 'm' };
  const r = roundDistance(m);
  if (r < 1000) return { value: String(r), unit: 'm' };
  // Kilometres from the raw value (avoids double rounding): 10 m steps below 10 km, else 100 m.
  if (m < 9995) return { value: (Math.round(m / 10) / 100).toFixed(2), unit: 'km' };
  return { value: (Math.round(m / 100) / 10).toFixed(1), unit: 'km' };
}

export function formatDistance(m: number | null | undefined): string {
  const p = formatDistanceParts(m);
  return `${p.value}${NBSP}${p.unit}`;
}

/** Speed rounded to 0.1 km/h (the displayed value). */
export function roundSpeed(kmh: number): number {
  return Math.round(kmh * 10) / 10;
}

/** "7.4", or "–.–" when unknown. Negative / tiny values show 0.0. */
export function formatSpeed(kmh: number | null | undefined): string {
  if (kmh == null || !Number.isFinite(kmh)) return '–.–';
  const r = roundSpeed(Math.max(0, kmh));
  return r.toFixed(1);
}

/** True when the displayed (0.1-rounded) speed exceeds the limit (10.0 is still OK). */
export function isSpeeding(kmh: number | null | undefined, limitKmh: number | null | undefined): boolean {
  if (kmh == null || limitKmh == null || !Number.isFinite(kmh)) return false;
  return roundSpeed(kmh) > limitKmh;
}

/** "±3.2 m" (one decimal below 10 m, integer above). */
export function formatAccuracy(m: number | null | undefined): string {
  if (m == null || !Number.isFinite(m)) return '±–';
  return `±${m < 10 ? m.toFixed(1) : Math.round(m).toString()}${NBSP}m`;
}

export function gpsWaitingText(accuracy: number | null, thresholdM: number): string {
  const target = `(Ziel ≤${NBSP}${formatNumber(thresholdM)}${NBSP}m)`;
  if (accuracy == null) return `Warte auf GPS-Signal … ${target}`;
  return `Warte auf genaues GPS: ${formatAccuracy(accuracy)} ${target}`;
}

/** Plain number without trailing ".0" for integers. */
export function formatNumber(v: number, digits = 1): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(digits);
}

/** "+3.2°", "−1.5°", "0.0°" */
export function formatSignedDeg(deg: number, digits = 1): string {
  const r = Number(deg.toFixed(digits));
  if (r === 0) return `${(0).toFixed(digits)}°`;
  return `${r > 0 ? '+' : MINUS}${Math.abs(r).toFixed(digits)}°`;
}

/** "Build 23.09.2026 14:05" in Europe/Zurich local time; '' for an invalid/missing timestamp. */
export function formatBuildStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('de-CH', {
    timeZone: 'Europe/Zurich',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `Build ${get('day')}.${get('month')}.${get('year')} ${get('hour')}:${get('minute')}`;
}

export const ZONE_LABELS: Record<ZoneKind, string> = {
  inner: 'Innere Uferzone',
  outer: 'Äussere Uferzone',
  open: 'Freie Wasserfläche',
  land: 'An Land',
  nodata: 'Keine amtlichen Daten',
};

export function zoneLabel(zone: ZoneKind): string {
  return ZONE_LABELS[zone];
}

export function extendedLabel(note: string | null | undefined): string {
  const n = note?.trim();
  return n ? `Erweiterte Uferzone: ${n}` : 'Erweiterte Uferzone';
}

/**
 * Parse "47.05, 8.31" (as copied from Google Maps) and a few variants:
 * "47.05 8.31", "47.05;8.31", "47.0535° N, 8.3155° E". Returns null if invalid.
 */
export function parseLatLon(text: string): LatLon | null {
  const m = text
    .trim()
    .match(
      /^\(?\s*(-?\d{1,3}(?:\.\d+)?)\s*°?\s*([NS])?\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*°?\s*([EOW])?\s*\)?$/i,
    );
  if (!m) return null;
  let lat = Number(m[1]);
  let lon = Number(m[3]);
  if (m[2]?.toUpperCase() === 'S') lat = -Math.abs(lat);
  if (m[4]?.toUpperCase() === 'W') lon = -Math.abs(lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

export function formatLatLon(p: LatLon): string {
  return `${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}`;
}

/** Counts taps; `tap()` returns true on the n-th tap within the time window, then resets. */
export class TapCounter {
  private times: number[] = [];
  constructor(
    private readonly count = 5,
    private readonly windowMs = 3000,
  ) {}

  tap(now: number): boolean {
    this.times = this.times.filter((t) => now - t <= this.windowMs);
    this.times.push(now);
    if (this.times.length >= this.count) {
      this.times = [];
      return true;
    }
    return false;
  }

  reset(): void {
    this.times = [];
  }
}

/**
 * Horizontal drag in HUD-local coordinates for a HUD rotated by `rotationDeg`
 * (CSS rotate, clockwise). Screen deltas are projected on the HUD's x axis.
 */
export function localDragDx(dxScreen: number, dyScreen: number, rotationDeg: number): number {
  const r = (normDeg(rotationDeg) * Math.PI) / 180;
  return dxScreen * Math.cos(r) + dyScreen * Math.sin(r);
}

/** Yaw trim delta (deg) for a horizontal drag: dx / width * 60°. */
export function dragToYawDeg(dxLocal: number, widthLocal: number): number {
  if (!(widthLocal > 0)) return 0;
  return (dxLocal / widthLocal) * 60;
}
